<template>
    <div v-if="iconData && iconData?.paths" class="icon-holder" :class="sizeClass">
        <svg :width="iconData.width" :height="iconData.height" :viewBox="iconData.viewBox" :class="svgClasses"
            :style="{ fill: color, ...transformStyle }">
            <g v-for="(path, index) in iconData.paths" :key="index">
                <path :d="path.d" :stroke="path.stroke" :strokeLinecap="path.strokeLinecap"
                    :strokeWidth="path.strokeWidth" :transform="path.transform" />
            </g>
        </svg>
    </div>
    <div v-else-if="iconData && iconData?.raw" class="icon-holder" :class="sizeClass">
        <div v-html="iconData.raw" :style="transformStyle"></div>
    </div>
    <span v-else-if="loading">Loading...</span>
    <span v-else>Invalid Icon:<{{ props.icon }}></span>
</template>

<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { icons } from './icons';

const iconI = Object.keys(icons).reduce((acc, key) => {
    acc[key.toLowerCase()] = icons[key];
    return acc;
}, {});



const svgClasses = computed(() => {
    return {
        'zs-icon': true,
        'custom-color': props.color != '',
        'zs-icon--spin': props.spin && props.spinDirection === 'normal',
        'zs-icon--spin-reverse': props.spin && props.spinDirection === 'reverse'
    };
});

const props = defineProps({
    color: { type: String, default: '' },
    icon: { type: String, required: true },
    size: { type: String, default: 'default' }, // Keep size prop
    rotation: { type: [Number, String], default: 0 }, // Add rotation prop in degrees
    spin: { type: Boolean, default: false },
    spinDirection: { type: String, default: 'normal' }, // 'normal' or 'reverse'
    flipH: { type: Boolean, default: false },
    flipV: { type: Boolean, default: false }
});

const iconData = ref(null);
const loading = ref(false);
const error = ref(false);

// Make color available to the template
const color = computed(() => props.color);

// Compute CSS class and style for size and rotation
const sizeClass = computed(() => {
    return {
        [`zs-icon--${props.size}`]: props.size !== 'default', // Apply class if size is not default
    };
});

// Computed style for rotation and flipping
const transformStyle = computed(() => {
    const transforms = [];
    if (props.rotation) {
        transforms.push(`rotate(${props.rotation}deg)`);
    }
    const scaleX = props.flipH ? -1 : 1;
    const scaleY = props.flipV ? -1 : 1;
    if (scaleX !== 1 || scaleY !== 1) {
        transforms.push(`scale(${scaleX}, ${scaleY})`);
    }
    return transforms.length ? { transform: transforms.join(' ') } : {};
});

// Watch for changes to props.icon and update the icon data accordingly
watch(() => props.icon, async (newIcon) => {
    loading.value = true;
    try {
        const lowerCaseIcon = newIcon.toLowerCase();
        if (iconI[lowerCaseIcon]) {
            iconData.value = iconI[lowerCaseIcon];
        } else {
            error.value = true;
            console.error(`Icon "${newIcon}" does not exist`);
        }
    } catch (e) {
        error.value = true;
        console.error(e);
    } finally {
        loading.value = false;
    }
}, { immediate: false });

onMounted(async () => {
    loading.value = true;
    try {
        const lowerCaseIcon = props.icon.toLowerCase();
        if (iconI[lowerCaseIcon]) {
            iconData.value = iconI[lowerCaseIcon]; // Now always use default (or only) icon data
        } else {
            error.value = true;
            console.error(`Icon "${props.icon}" does not exist`);
        }
    } catch (e) {
        error.value = true;
        console.error(e);
    } finally {
        loading.value = false;
    }
});
</script>

<style>
:root {
    /* Icon base styles */
    --zs-icon-holder-display: flex;
    --zs-icon-holder-align-items: center;

    /* Icon scaling */
    --zs-icon-scale-x-small: 0.5;
    --zs-icon-scale-small: 0.7;
    --zs-icon-scale-medium: 1;
    --zs-icon-scale-large: 1.3;
    --zs-icon-scale-x-large: 1.6;
}

.icon-holder {
    display: var(--zs-icon-holder-display);
    align-items: var(--zs-icon-holder-align-items);
}

.zs-icon.custom-color {
    fill: v-bind(color);
}

/* CSS Classes for scaling */
.zs-icon--small {
    transform: scale(var(--zs-icon-scale-small));
}

.zs-icon--medium {
    transform: scale(var(--zs-icon-scale-medium));
}

.zs-icon--large,
.zs-icon--big {
    transform: scale(var(--zs-icon-scale-large));
}

/* You can add more sizes like x-small, x-large, etc. */
.zs-icon--x-small {
    transform: scale(var(--zs-icon-scale-x-small));
}

.zs-icon--x-large {
    transform: scale(var(--zs-icon-scale-x-large));
}

.zs-icon--spin {
    animation: spin 2s linear infinite;
}

.zs-icon--spin-reverse {
    animation: spin-reverse 2s linear infinite;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

@keyframes spin-reverse {
    from {
        transform: rotate(360deg);
    }
    to {
        transform: rotate(0deg);
    }
}
</style>
